import React from 'react';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon from '../../utils/getMuiIcon';
import useSubscriptionInfoData, { SubscriptionStatus } from '../../utils/useSubscriptionInfoData';
import SubscriptionStatusAlert from './SubscriptionStatusAlert';

interface SubscriptionDetailsProps {
  isBasicUserWithoutSubscription: boolean;
  endDate: string;
  subscriptionStatus: SubscriptionStatus;
  onClickUpgrade: () => void;
  onShowManageSubscriptionDialog: () => void;
}

const SubscriptionDetails = ({
  isBasicUserWithoutSubscription,
  endDate,
  subscriptionStatus,
  onClickUpgrade,
  onShowManageSubscriptionDialog,
}: SubscriptionDetailsProps) => {
  const { t } = useTranslation();
  const {
    isBusinessUser,
    allowAutomaticCancellation,
    productName,
    price,
    priceText,
    discountText,
    discountMonths,
    reducedPrice,
    pauseStartDate,
    pauseEndDate,
    isInTrial,
    withdrawalSubmittedAt,
  } = useSubscriptionInfoData();

  if (isBasicUserWithoutSubscription) {
    return (
      <Grid item>
        <Box sx={{ display: 'grid', gap: spaces.lg }}>
          <Typography
            variant='body1'
            color={palette.text.disabled}
          >
            {t('No active subscription')}
          </Typography>
          <Button
            variant='contained'
            size='large'
            color='primary'
            key='upgrade-button'
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              width: 'fit-content',
              paddingX: spaces.lg,
              paddingY: spaces.xs,
            }}
            startIcon={
              <SvgIcon
                sx={{ marginRight: spaces.xs }}
                component={getMuiIcon('upgradeSubscription')}
                viewBox='0 0 24 24'
              />
            }
            onClick={onClickUpgrade}
          >
            {t('Upgrade Plan')}
          </Button>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item>
      <Box>
        <Typography
          variant='h6'
          color={palette.text.primary}
          sx={{
            marginBottom: spaces.xxs,
          }}
        >
          {discountText ? `${productName} — ${t('discount', { discountText })}` : productName}
        </Typography>
        <Typography>
          {t(priceText, { discountText, discountMonths, price, reducedPrice })}
        </Typography>
        {subscriptionStatus === SubscriptionStatus.Active && allowAutomaticCancellation && (
          <Button
            variant='outlined'
            color='inherit'
            size='large'
            onClick={onShowManageSubscriptionDialog}
            sx={{
              marginTop: spaces.xl,
            }}
          >
            {isInTrial ? t('Cancel Trial Subscription') : t('Manage Subscription')}
          </Button>
        )}
        <SubscriptionStatusAlert
          subscriptionStatus={subscriptionStatus}
          endDate={endDate}
          pauseStartDate={pauseStartDate}
          pauseEndDate={pauseEndDate}
          isBusinessUser={isBusinessUser}
          withdrawalSubmittedAt={withdrawalSubmittedAt}
        />
      </Box>
    </Grid>
  );
};

export default SubscriptionDetails;
